import { logger } from '@adornis/base/logging.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Timeframe } from '@adornis/bh-timeframes/entities/Timeframe.js';
import '@adornis/buchungsheld-components/bh-stat-card.js';
import { baseParticipationAggregation } from '@adornis/buchungsheld-db/api/statistic.js';
import '@adornis/charts/apexcharts/client/apex-chart.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-section.js';
import '@adornis/internationalization/client/x-tel-input.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map } from 'rxjs/operators';
import { BHFilter } from '../../entities/bh-filter.js';
import { Participation } from '../../entities/Participation.js';
import { Product } from '../../entities/products.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import { Workshop } from '../../entities/workshop.js';
import { ParticipationState } from '../../enums/enums.js';
import { fillInEmpty } from '../../util.js';
import type { InstitutionStatisticsPlugin } from '../plugin-types.js';
import { IcsrmDesiredDegreeEnum, IcsrmUniEnum } from '../stammdaten/icsrm-stammdaten-plugin.js';
import { getApexLineChartOptions, getApexStackedBarChartOptions } from './chart-helpers.js';
import { DefaultInstitutionStatistics, fromPromiseStartWithNull } from './default-institiiution-statistics-plugin.js';
import { icsrmGetCitizenshipFromCountry, icsrmGetDaadRegionFromCountry } from './icsrm-helper.js';

export const getIcsrmGenderDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getIcsrmGenderDistribution',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            state: ParticipationState.IN,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        // count by gender
        {
          $group: {
            _id: {
              gender: '$userStammdaten.gender',
            },
            total: {
              $sum: 1,
            },
            gender: {
              $first: '$userStammdaten.gender',
            },
          },
        },
      ];

      const participatonCollection = await getRawCollection(Participation._collectionName);
      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result.filter(group => !!group.gender).map(group => [group.gender, group.total]);
    };
  },
});

export const getDaadCountryDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getDaadCountryDistribution',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const participatonCollection = await getRawCollection(Participation._collectionName);
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            state: ParticipationState.IN,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        // count by country
        {
          $group: {
            _id: {
              country: '$userStammdaten.countryOfBirth',
            },
            total: {
              $sum: 1,
            },
            country: {
              $first: '$userStammdaten.countryOfBirth',
            },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      const mappedGroups = result
        .filter(group => !!group.country)
        .reduce((acc, group) => {
          const country = group.country;
          const count = group.total;
          const region = icsrmGetDaadRegionFromCountry(country);
          if (!region) return acc; // there are some countries that should be ignored
          acc[region] = acc[region] ?? [region, 0];
          acc[region][1] += count;
          return acc;
        }, {});

      return Object.values(mappedGroups);
    };
  },
});

export const getCitizenshipDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getCitizenshipDistribution',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const participatonCollection = await getRawCollection(Participation._collectionName);
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            state: ParticipationState.IN,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        // count by country
        {
          $group: {
            _id: {
              country: '$userStammdaten.nationality',
            },
            total: {
              $sum: 1,
            },
            country: {
              $first: '$userStammdaten.nationality',
            },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      const mappedGroups = result
        .filter(group => !!group.country)
        .reduce((acc, group) => {
          const country = group.country;
          const count = group.total;
          const region = icsrmGetCitizenshipFromCountry(country);
          if (!region) return acc; // there are some countries that should be ignored
          acc[region] = acc[region] ?? [region, 0];
          acc[region][1] += count;
          return acc;
        }, {});

      logger.debug({ mappedGroups }, 'groups');

      return Object.values(mappedGroups);
    };
  },
});

export const getUniDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getUniDistribution',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const participatonCollection = await getRawCollection(Participation._collectionName);
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            state: ParticipationState.IN,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        // count by Hochschule
        {
          $group: {
            _id: {
              uni: '$userStammdaten.currentUni',
            },
            total: {
              $sum: 1,
            },
            uni: {
              $first: '$userStammdaten.currentUni',
            },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result
        .filter(group => !!group.uni)
        .map(group => [group.uni, group.total])
        .sort((a, b) => {
          // sort by enum order
          const index_a = Object.keys(IcsrmUniEnum).indexOf(a[0]);
          const index_b = Object.keys(IcsrmUniEnum).indexOf(b[0]);
          return index_a - index_b;
        });
    };
  },
});

export const getDesiredDegreeDistribution = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getDesiredDegreeDistribution',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async () => {
      const participatonCollection = await getRawCollection(Participation._collectionName);
      const aggregation = [
        ...baseParticipationAggregation,
        {
          $match: {
            state: ParticipationState.IN,
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        // count by Hochschule
        {
          $group: {
            _id: {
              degree: '$userStammdaten.desiredDegree',
            },
            total: {
              $sum: 1,
            },
            degree: {
              $first: '$userStammdaten.desiredDegree',
            },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      return result
        .filter(group => !!group.degree)
        .map(group => [group.degree, group.total])
        .sort((a, b) => {
          // sort by enum order
          const index_a = Object.keys(IcsrmDesiredDegreeEnum).indexOf(a[0]);
          const index_b = Object.keys(IcsrmDesiredDegreeEnum).indexOf(b[0]);
          return index_a - index_b;
        });
    };
  },
});

export const getUnterbuchungenPerWeek = registerQuery({
  type: () => [[String, Int]],
  operationName: 'getUnterbuchungenPerWeek',
  params: [
    { type: () => BHFilter, name: 'filter' },
    { type: () => Boolean, name: 'onlyForpartiicipantsWithImmigrationHistory' },
    { type: () => Int, name: 'limit' },
  ],
  resolve: (filter: BHFilter, onlyForpartiicipantsWithImmigrationHistory: boolean, limit: number) => {
    return async () => {
      const productCollection = await getRawCollection(Product._collectionName);
      const aggregation = [
        {
          $match: {
            _class: Workshop._class,
            ...(filter.institutionID ? { institutionID: filter.institutionID } : {}),
            ...(filter.timeframeID ? { timeframeID: filter.timeframeID } : {}),
            appointments: { $elemMatch: { $exists: true } }, // at least one appointment
            'appointments.0.start': { $exists: true },
          },
        },
        // calculate number of participants
        {
          $lookup: {
            from: Participation._collectionName,
            localField: '_id',
            foreignField: 'productID',
            pipeline: [
              // lookup stammdaten
              {
                $lookup: {
                  from: UserStammdaten._collectionName,
                  localField: 'userID',
                  foreignField: 'userID',
                  as: 'userStammdaten',
                },
              },
              // filter stammdaen for product
              {
                $addFields: {
                  userStammdaten: {
                    $first: {
                      $filter: {
                        input: '$userStammdaten',
                        as: 'userStammdaten',
                        cond: {
                          $eq: ['$$userStammdaten.productID', '$participations.productID'],
                        },
                      },
                    },
                  },
                },
              },
            ],
            as: 'participations',
          },
        },

        // maybe filter out non immigration participations
        ...(onlyForpartiicipantsWithImmigrationHistory
          ? [
              {
                $addFields: {
                  participations: {
                    $filter: {
                      input: '$participations',
                      as: 'participation',
                      cond: {
                        $or: [
                          { $eq: ['$$participation.userStammdaten.isImmigrant', true] },
                          { $ne: ['$$participation.countryOfBirth', 'DE'] },
                        ],
                      },
                    },
                  },
                },
              },
            ]
          : []),

        // get stuff
        {
          $addFields: {
            start: { $first: '$appointments.start' },
            participationCount: { $size: '$participations' },
          },
        },

        // calc ratio
        {
          $addFields: {
            ratio: { $min: [{ $multiply: [100, { $divide: ['$participationCount', '$capacity'] }] }, 100] },
          },
        },

        // group by week
        {
          $project: {
            capacity: 1,
            participationCount: 1,
            ratio: 1,
            week: {
              $week: '$start',
            },
            year: {
              $year: '$start',
            },
          },
        },
        {
          $group: {
            _id: {
              week: '$week',
              year: '$year',
            },
            total: {
              $avg: '$ratio',
            },
            week: { $first: '$week' },
            year: { $first: '$year' },
          },
        },
      ];

      const result = await productCollection
        .aggregate(aggregation)
        .sort({ year: -1, week: -1 }) // sort desc to get most current results
        .limit(limit)
        .toArray();

      const registrationsOverTime = fillInEmpty(result as any);
      return registrationsOverTime.slice(-1 * limit).map(({ week, total }) => [week, total]);
    };
  },
});

@Entity()
export class TimeframeParticipationCountByImigrationHistory extends AdornisEntity {
  public static override _class = 'TimeframeParticipationCountByImigrationHistory';

  @Field(type => String)
  public timeframeID!: string;

  @DataLoadedField(type => Timeframe, doc => ({ _id: doc.timeframeID }))
  public timeframe!: Timeframe;

  @Field(type => [[String, Int]])
  public counts!: Array<[string, number]>;
}

export const getParticipationCountGroupedByTimeframeAndImigrationHistory = registerQuery({
  type: () => [TimeframeParticipationCountByImigrationHistory],
  operationName: 'getParticipationCountGroupedByTimeframeAndImigrationHistory',
  params: [{ type: () => BHFilter, name: 'filter' }],
  resolve: (filter: BHFilter) => {
    return async (gqlFields: BaseQLSelectionSet<TimeframeParticipationCountByImigrationHistory>) => {
      const participatonCollection = await getRawCollection('participations-materialized');
      const aggregation = [
        {
          $match: {
            ...(filter.institutionID ? { 'product.institutionID': filter.institutionID } : {}),
            ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
          },
        },
        // set default value if field is null
        {
          $addFields: {
            isImmigrant: { $eq: ['$nachrueckPriority', 'high'] },
          },
        },
        // group by timeframe and immigration history
        {
          $group: {
            _id: {
              timeframeID: '$product.timeframeID',
              isImmigrant: '$isImmigrant',
            },
            total: {
              $sum: 1,
            },
            timeframeID: {
              $first: '$product.timeframeID',
            },
            isImmigrant: {
              $first: '$isImmigrant',
            },
          },
        },
      ];

      const result = await participatonCollection.aggregate(aggregation).toArray();

      const counts = result.reduce((acc, r) => {
        const timeframeID = r.timeframeID;
        if (!timeframeID) return acc;
        const tpCount =
          acc[timeframeID] || new TimeframeParticipationCountByImigrationHistory({ timeframeID, counts: [] });
        acc[timeframeID] = tpCount;
        tpCount.counts.push([!!r.isImmigrant, r.total]);
        return acc;
      }, {});

      return Object.values(counts);
    };
  },
});

@customElement('icsrm-institution-statistics')
class IcsrmInstitutionStatistics extends DefaultInstitutionStatistics {
  @state() private readonly genderDistribution = new RXController(
    this,
    this._filter.pipe(
      map(filter => getIcsrmGenderDistribution(filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly daadCountryDistribution = new RXController(
    this,
    this._filter.pipe(
      map(filter => getDaadCountryDistribution(filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly uniDistribution = new RXController(
    this,
    this._filter.pipe(
      map(filter => getUniDistribution(filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly desiredDegreDistribution = new RXController(
    this,
    this._filter.pipe(
      map(filter => getDesiredDegreeDistribution(filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly citizenshipDistribution = new RXController(
    this,
    this._filter.pipe(
      map(filter => getCitizenshipDistribution(filter)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly unterbuchungenPerWeek = new RXController(
    this,
    this._filter.pipe(
      map(filter => getUnterbuchungenPerWeek(filter, false, 50)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly unterbuchungenPerWeekForParticipationsWithImmigrationHistory = new RXController(
    this,
    this._filter.pipe(
      map(filter => getUnterbuchungenPerWeek(filter, true, 50)()),
      fromPromiseStartWithNull(),
    ),
  );

  @state() private readonly participationCountByTimeframeAndImigrationHistory = new RXController(
    this,
    this._filter.pipe(
      map(filter =>
        getParticipationCountGroupedByTimeframeAndImigrationHistory(filter)({
          timeframe: Timeframe.allFields,
          counts: 1,
        }),
      ),
      fromPromiseStartWithNull(),
    ),
  );

  renderGenderDistributionCard() {
    const genderDistribution = this.genderDistribution.value;
    if (!genderDistribution) return nothing;
    return this.renderPieChartCard(
      this._translation.translate('statistics', 'icsrm_gender_distribution'),
      genderDistribution.map(d => parseInt(d[1])),
      genderDistribution.map(d => d[0]).map(d => this._translation.translate('enums', d)),
    );
  }

  renderUnterbuchungenOverTimeCard() {
    if (!this.unterbuchungenPerWeek.value) return html`<x-loader></x-loader>`;
    return html`<bh-stat-card
      page-break
      advanced
      name=${this._translation.translate('statistics', 'icsrm_unterbuchungen_over_time')}
      .value=${html`
        <apex-chart
          .options=${getApexLineChartOptions(
            this,
            this.unterbuchungenPerWeek.value?.map(([week, total]) => parseInt(total ?? 0)),
            this.unterbuchungenPerWeek.value?.map(([week, total]) => 'KW' + week),
            'Ausbuchung (%)',
          )}
        ></apex-chart>
      `}
    ></bh-stat-card> `;
  }

  renderUnterbuchungeWithImmigrationHistorynOverTimeCard() {
    if (!this.unterbuchungenPerWeekForParticipationsWithImmigrationHistory.value) return html`<x-loader></x-loader>`;
    const unterbuchungen = this.unterbuchungenPerWeekForParticipationsWithImmigrationHistory.value;
    return html`<bh-stat-card
      page-break
      advanced
      name=${this._translation.translate('statistics', 'icsrm_unterbuchungen_over_time_with_immigration_history')}
      .value=${html`
        <apex-chart
          .options=${getApexLineChartOptions(
            this,
            unterbuchungen?.map(([week, total]) => parseInt(total ?? 0)),
            unterbuchungen?.map(([week, total]) => 'KW' + week),
            'Ausbuchung (%)',
          )}
          )}
        ></apex-chart>
      `}
    ></bh-stat-card> `;
  }

  renderCitizenshipDistributionCard() {
    const citizenshipDistribution = this.citizenshipDistribution.value;
    return this.renderPieChartCard(
      this._translation.translate('statistics', 'icsrm_citizenship_disribution'),
      citizenshipDistribution?.map(d => parseInt(d[1])),
      citizenshipDistribution?.map(d => d[0]),
    );
  }

  renderUniDistributionCard() {
    const uniDistribution = this.uniDistribution.value;
    if (!uniDistribution) return nothing;
    return this.renderPieChartCard(
      this._translation.translate('statistics', 'icsrm_uni_disribution'),
      uniDistribution.map(d => parseInt(d[1])),
      uniDistribution.map(d => d[0])?.map(d => this._translation.translate('enums', d)),
      true,
    );
  }

  renderDesiredDegreeDistributionCard() {
    const desiredDegreDistribution = this.desiredDegreDistribution.value;
    if (!desiredDegreDistribution) return nothing;
    return this.renderPieChartCard(
      this._translation.translate('statistics', 'icsrm_desired_degree_disribution'),
      desiredDegreDistribution?.map(d => parseInt(d[1])),
      desiredDegreDistribution?.map(d => d[0]).map(d => this._translation.translate('enums', d)),
      true,
    );
  }

  renderDaadCountryDistributionCard() {
    const daadCountryDistribution = this.daadCountryDistribution.value;
    if (!daadCountryDistribution) return nothing;
    return this.renderPieChartCard(
      this._translation.translate('statistics', 'icsrm_daad_country_disribution'),
      daadCountryDistribution?.map(d => parseInt(d[1])),
      daadCountryDistribution?.map(d => d[0]),
      true,
    );
  }

  renderParticipationsCountPerTimeframeAndImigrationHistory() {
    const c = this.colors;
    const _colors = [
      c.accent,
      c.secondary,
      c.tone.success,
      c.tone.warning,
      c.tone.error,
      { string: '#1fa384' },
      { string: '#4474b3' },
    ];
    const colors = _colors.map(c => c.string);

    const participations = this.participationCountByTimeframeAndImigrationHistory.value;

    const cardContent = (() => {
      if (!participations) return html`<x-loader></x-loader>`;
      return html` <apex-chart
        .options=${getApexStackedBarChartOptions(
          this,
          ['true', 'false'].map((key, i) => ({
            name: key === 'true' ? 'Teilnehmer mit Zuwanderungsgeschichte' : 'Teilnehmer ohne Zuwanderungsgeschichte',
            data: participations?.map(d => d.counts.find(([state, value]) => state === key)?.[1] ?? 0),
            color: colors[i % colors.length],
          })),
          participations?.map(d => d.timeframe.name),
        )}
      ></apex-chart>`;
    })();

    return html`
      <bh-stat-card
        advanced
        ${css({ gridColumn: this._size.width < 1500 ? 'span 1' : 'span 2' })}
        name=${this._translation.translate(
          'statistics',
          'icsrm_participation_count_per_timefrmae_and_immigration_history',
        )}
        .value=${cardContent}
      ></bh-stat-card>
    `;
  }

  override chartCards() {
    return [
      this.renderParticipantsCard(),
      this.renderTotalAppointmentsCard(),
      this.renderParticipatingParticipationCountCard(),
      this.renderParticipatedParticipationCountCard(),
      // this.renderParticipatingAndParticipatedParticipationCountCard(),
      this.renderSuspendedAndSignedOffAndWaitingParticipationCountCard(),
      this.renderAverageParticipationAndParticipatedParticipationsPerUserCard(),
      this.renderAverageSuspendedAndSignedOffAndWaitingParticipationsPerUser(),
      this.renderTotalWorkshopsCard(),
      // html`<br />`,
      this.renderProductRegistrationsOverTimeCard(),
      this.renderParticipationOverTimeCard(),
      this.renderGenderDistributionCard(),
      this.renderCitizenshipDistributionCard(),
      this.renderDaadCountryDistributionCard(),
      this.renderUniDistributionCard(),
      this.renderDesiredDegreeDistributionCard(),
      this.renderUnterbuchungenOverTimeCard(),
      this.renderUnterbuchungeWithImmigrationHistorynOverTimeCard(),
      this.renderParticipationsCountPerTimeframeAndImigrationHistory(),
      this.renderParticipationsCountPerStateCard(),
    ];
  }
}

export const icsrmInstitutionStatisticsPlugin: InstitutionStatisticsPlugin = {
  id: 'institution-statistics::icsrm',
  type: 'institution-statistics',
  render: (filter: BHFilter) => {
    return html` <icsrm-institution-statistics .filter=${filter}></icsrm-institution-statistics> `;
  },
};
